/**
 * Single source of truth for the "how many items need attention" number that the
 * Verification page headline AND the nav chip both display. They used to compute
 * this separately (the chip read only validation/latest and counted errors only;
 * the page merged latest+guide+live-dismissals and counted everything), so they
 * could — and did — disagree ("1" on the chip vs "5 items need your review" on
 * the page). Both now derive from `computeValidationCounts`, so they cannot.
 *
 * (Internal Firestore/identifier names stay "validation*"; only user-facing copy
 * says "Verification".)
 */
import { useEffect, useState } from "react";
import { onSnapshot, collection } from "firebase/firestore";
import { db } from "../../firebase";
import { validationLatestDoc, validationGuideDoc } from "../../constants/firestorePaths";
import { usePipelineReview } from "../corrections/usePipelineReview";
import { computeValidationCounts } from "./validationCountsCore";
import type { RunLike, DismissalLike, ValidationCounts } from "./validationCountsCore";

// The pure count logic lives in validationCountsCore (no React/Firebase) so it
// can be unit-tested; re-exported here for existing importers.
export { computeValidationCounts };
export type { ValidationCounts };

/** Subscribing hook for consumers (the nav chip) that don't already load the run
 *  docs. The page passes its own live latest/guide/dismissals to the pure fn. */
export function useValidationCounts(projectId?: string): ValidationCounts {
  const [latest, setLatest] = useState<RunLike | null>(null);
  const [guide, setGuide] = useState<RunLike | null>(null);
  const [dismissals, setDismissals] = useState<Map<string, DismissalLike>>(new Map());
  // Inc5 step F: pipeline-family decisions join the same number the
  // Verification page headline shows — the two can never disagree.
  const pipeline = usePipelineReview(projectId);

  useEffect(() => {
    if (!projectId) return;
    const u1 = onSnapshot(validationLatestDoc(projectId),
      (s) => setLatest(s.exists() ? (s.data() as RunLike) : null), () => setLatest(null));
    const u2 = onSnapshot(validationGuideDoc(projectId),
      (s) => setGuide(s.exists() ? (s.data() as RunLike) : null), () => {});
    const u3 = onSnapshot(collection(db, "projects", projectId, "validationDismissals"), (snap) => {
      const m = new Map<string, DismissalLike>();
      snap.forEach((d) => { const x = d.data() as DismissalLike; if (x?.key) m.set(x.key, x); });
      setDismissals(m);
    }, () => {});
    return () => { u1(); u2(); u3(); };
  }, [projectId]);

  return computeValidationCounts(latest, guide, dismissals, { openCount: pipeline.openCount });
}
